import * as _ from 'underscore';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Logger } from '@core/lib/services';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { AppConfigService } from '@app/common/app-config.service';
import { ModalConfig } from '@core/lib/components/modal/modal-config';
import { ModalRef } from '@core/lib/components/modal/modal-ref';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { MessageService } from '@core/lib/services/message.service';
import { DiConfigService } from '@app/di/di-config.service';
import { DOCUMENT } from '@angular/common';
import { DatabaseList } from './../../../../data-model/list/database-list';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from '@app/common/app-constants';
import { NativeAcademicWorkflowService } from '@app/di/native-academic-workflow.service';
import { Subscription } from 'rxjs';
import { DialogType } from '@app/di/integrations/dialog-type';
import { WorkListNavigatorService } from '@app/ui/common/work-list-navigator/work-list-navigator.service';
import { NavigatorList } from '@app/data-model/list/navigator-list';
import { ToDoList, ToDoListId } from '@app/data-model/list/to-do-list';
import { UtilService } from '@app/util/util.service';
import { isValidValue } from '@core/lib/helpers/helpers';

const log = new Logger('UserPreferenceComponent');

@Component({
  selector: 'app-user-preference',
  templateUrl: './user-preference.component.html',
  styleUrls: ['./user-preference.component.scss']
})
export class UserPreferenceComponent implements OnInit, OnDestroy {
  userPreferences;
  defaultWorklist: any;
  allWorklists: any;
  listOfWorklists = [];
  preferenceOnTabClose;
  preferenceOnSignout;
  preferenceOnTakeControl;
  isLightThemeEnabled = false;
  listType = ToDoListId;
  listCustom: Array<NavigatorList> = [];
  listToDo: Array<NavigatorList> = [];
  listSystem: Array<NavigatorList> = [];
  coReadersReady = false;
  coReaders: any[] = [];
  coReader: any = undefined;
  isExternalApp = false;

  private subscriptions: Subscription[] = [];
  defaultDepartment: any[] = [];
  listOfDepartment: any[] = [];
  defaultDepartmentReady = false;
  get coReaderEnabled(): boolean {
    return this.coReadersReady && this.userPreferenceService.userPreferences.nativeAcademicWorkflow;
  }

  constructor(
    private cd: ChangeDetectorRef,
    public config: ModalConfig,
    public modalRef: ModalRef,
    @Inject(DOCUMENT) private document: Document,
    private appConfigService: AppConfigService,
    public userPreferenceService: UserPreferenceService,
    private userPrivilegesService: UserPrivilegesService,
    private messageService: MessageService,
    private diConfigService: DiConfigService,
    private translate: TranslateService,
    private nativeAcademicWorkflowService: NativeAcademicWorkflowService,
    private workListNavigatorService: WorkListNavigatorService,
    private utilService: UtilService,
    private searchFilterCriteriaService: SearchFilterCriteriaService
  ) {}

  sortByNameProperty = (a, b) => ((a || {}).name || '').localeCompare((b || {}).name || '');
  diConfig = this.diConfigService.getDiConfig();
  reportingIntegrationType = this.diConfig && this.diConfig.REPORTING_INTEGRATION_TYPE;
  init() {
    this.isExternalApp = isValidValue(this.utilService.externalLoginApp);
    this.defaultWorklist = this.userPreferenceService.userDefaultWorklist;
    this.isLightThemeEnabled = !!this.userPreferenceService.getUserLevelPreferenceFromCache('LightsMode');
    this.nativeAcademicWorkflowService.getNawUserList(undefined, DialogType.DEFAULT_COREADER).subscribe(readers => {
      const coreader = this.userPreferenceService.getUserLevelPreferenceFromCache('defaultCoReader');
      this.coReaders = readers;
      this.coReader = this.coReaders.find(cr => cr.userName === coreader);
      this.coReadersReady = true;
    });
    this.subscriptions.push(
      this.messageService.on('userLevelPreferenceUpdated::defaultCoReader').subscribe(coreader => {
        this.coReader = undefined;
        if (this.coReaders?.length) {
          this.coReader = this.coReaders.find(cr => cr.userName === coreader);
        }
      }),
      this.workListNavigatorService.getCustomWorkLists().subscribe(customWorkLists => {
        customWorkLists = customWorkLists.filter(list => {
          return !list.isLinked;
        });
        _.each(customWorkLists, (list: DatabaseList) => {
          list.name = list.displayName;
        });
        this.listCustom = customWorkLists.sort(this.nameSort);
        this.setListOfWorklists();
      }),
      this.workListNavigatorService.getToDoWorkLists().subscribe(listToDo => {
        listToDo = listToDo.filter(list => {
          return !list.isHidden;
        });
        _.each(listToDo, (list: ToDoList) => {
          list.name = list.displayName;
        });
        this.listToDo = listToDo.sort(this.nameSort);
        this.setListOfWorklists();
      }),
      this.workListNavigatorService.getSystemWorkLists().subscribe(systemWorkLists => {
        _.each(systemWorkLists, (list: DatabaseList) => {
          list.name = list.displayName;
        });
        this.listSystem = systemWorkLists.sort(this.nameSort);
        this.setListOfWorklists();
      }),
      this.searchFilterCriteriaService.getLoadedObserver().subscribe(lookUpLoaded => {
        if (lookUpLoaded && this.isSRWorkflowEnabled) {
          this.listOfDepartment = this.searchFilterCriteriaService
            .getDepartments()
            ?.sort(this.sortByNameProperty)
            .filter(dept => dept.name.toLowerCase() !== 'unspecified');
          this.userPreferenceService.getSRDeptObserver().subscribe(isDeptset => {
            if (isDeptset) {
              const deparmentCode = this.userPreferenceService.getSRDepartment();
              this.defaultDepartment = this.listOfDepartment.find(item => {
                return item.code?.toLowerCase() === deparmentCode?.toLowerCase();
              });
              this.defaultDepartmentReady = true;
              this.cd.detectChanges();
            }
          });
        }
      })
    );
  }

  // Data Listeners
  private nameSort(s1: DatabaseList | ToDoList, s2: DatabaseList | ToDoList) {
    return s1.displayName.toLowerCase() < s2.displayName.toLowerCase() ? -1 : 1;
  }

  // Data Manipulators
  setListOfWorklists() {
    this.listOfWorklists = [];
    const systemList: NavigatorList[] = this.listToDo.concat(this.listSystem);
    if (this.listCustom?.length > 0) {
      this.listOfWorklists.push({
        label: this.translate.instant('user.preference.custom.worklist'),
        items: this.listCustom
      });
    }
    this.listOfWorklists.push({
      label: this.translate.instant('user.preference.system.worklist'),
      items: systemList
    });
  }

  updateDefaultWorklist(selectedItem) {
    this.userPreferenceService.updateDefaultWorklist(selectedItem, this.defaultWorklist?.worklistId?.toString()).then(
      () => {
        this.defaultWorklist = selectedItem;
        if (!selectedItem.isPinned) {
          this.workListNavigatorService.setPin(selectedItem);
        }
      },
      error => {
        log.error('UserPreferenceComponent: ' + JSON.stringify(error));
      }
    );
  }

  updateDefaultDepartment(selectedItem) {
    this.defaultDepartment = selectedItem;
    const SR_DEPT = appConstants.USER_PREFERENCE_KEYS.SR_DEPARTMENT;
    this.userPreferenceService.createOrUpdateUserLevelPreference({ [SR_DEPT]: this.defaultDepartment['code'] }, true);
  }

  dictatePrivilege = this.userPrivilegesService.hasPrivilege('Dictate');
  assignmentPrivilege = this.userPrivilegesService.hasPrivilege('ID_Assignment');
  aiFindingsEnable = this.userPreferenceService.getAiFindingsEnable();
  nativeAcademicWorkflowEnabled = this.nativeAcademicWorkflowService.isAppConfigActive();
  isInstituteSeparationEnabled = this.appConfigService.isInstituteSeparationEnabled();
  showAutoMinimize = this.reportingIntegrationType === appConstants.reportingAppName.PS;
  isReportingIntegrationConfigured = this.reportingIntegrationType !== appConstants.reportingAppName.None;
  isSaveOnTabClosureSupported =
    this.reportingIntegrationType === appConstants.reportingAppName.PS ||
    this.reportingIntegrationType === appConstants.reportingAppName.NPR;
  isEvConfigured = this.diConfigService.integrationConfig?.Ev;

  isSRWorkflowEnabled = this.reportingIntegrationType === appConstants.reportingAppName.NPR;

  ngOnInit(): void {
    this.userPreferenceService.getUserPreferenceByKey();
    this.init();

    this.userPreferences = this.userPreferenceService.userPreferences;
    this.preferenceOnTabClose = this.userPreferences['unassign-on-tab-closure'];
    this.preferenceOnSignout = this.userPreferences['unassign-on-signout'];
    this.preferenceOnTakeControl = this.userPreferences['save-report-on-release-control'];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscriber => {
      subscriber.unsubscribe();
    });
  }

  // Update or create the user preference for the logged in user.
  // It is to update the user preference property
  // when user click on it, if property not exists corresponds to the user in Database, create an entry.
  updateUserPreference(preferenceName, preferenceValue) {
    this.userPreferenceService.updateUserPreference(preferenceName, preferenceValue);
  }

  autoAdvanceToggle(autoAdvanceCheck) {
    this.userPreferenceService.autoAdvanceToggle(autoAdvanceCheck);
  }

  ptHistoryToggle(ptHistoryConfCheck) {
    this.userPreferenceService.userPreferences[ptHistoryConfCheck] = this.userPreferences[ptHistoryConfCheck];
    this.userPreferenceService.updatePtHistoryPreference();
  }

  aiFindingsToggle() {
    this.updateUserPreference('show-ai-findings', this.userPreferences.showAiFindings);
  }

  closeModal(evt, response?) {
    evt.stopPropagation();
    this.modalRef.close(response);
  }

  coReaderValueChange(coReader) {
    this.coReader = coReader;
    const username = this.coReader?.userName || '';
    this.userPreferenceService.createOrUpdateUserLevelPreference({ defaultCoReader: username }, true);
  }

  toggleTheme() {
    this.userPreferenceService.createOrUpdateUserLevelPreference({ LightsMode: this.isLightThemeEnabled });
    this.userPreferenceService.applyTheme();
  }
}
